import { useEffect, useMemo, useState } from 'react';
import TextWinds from '../../../components/common/TextWinds';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../store/hook';
import { selectLmsLists } from '../../../store/features/lmsSlice';
import {
  getVoucherList,
  getVoucherProductList,
} from '../../../api/utils/lms/voucher/api';
import { VoucherItem } from '../../../constants/types/lms/voucher/type';
import { openAlert, openModal } from '../../../store/features/modalSlice';
import { getErrorMessages, openNewWindow } from '../../../api/utils/util-func';
import ModalAlert from '../../../components/modal/ModalAlert';
import { LoadingSpinner } from '../../../components/Loading/LoadingSpinner';
import VoucherHistoryPanel from '../../../components/lms/voucher/VoucherHistoryPanel';
import VoucherHistoryBottomSheet from '../../../components/lms/voucher/VoucherHistoryBottomSheet';
import VoucherListPanel from '../../../components/lms/voucher/VoucherListPanel';
import { Button } from 'antd';
import { CourseId } from '../../../constants/types/lms/type';

/**
 * 개편 LMS (교사) - 이용권 관리 페이지
 */
const VoucherManagement = () => {
  const { classId } = useParams();

  const lmsLists = useAppSelector(selectLmsLists);

  const courseType = useMemo(() => {
    switch (lmsLists.course.id) {
      case CourseId.BASIC:
        return 'BASIC';
      case CourseId.WMW:
        return 'WMW';
      case CourseId.HFHJ:
        return 'HFHJ';
      default:
        return null;
    }
  }, [lmsLists]);

  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [voucherList, setVoucherList] = useState<VoucherItem[]>([]);

  useEffect(() => {
    if (!lmsLists.course || !classId) return;

    const fetchVoucherList = async () => {
      try {
        setIsLoading(true);

        const responseVoucherList = await getVoucherList({
          classId,
          courseId: lmsLists.course?.id,
        });

        const responseVoucherProductList = await getVoucherProductList();

        const voucherListWithProductId = responseVoucherList.map((voucher) => {
          const matchedVoucherProduct = responseVoucherProductList.items.find(
            (voucherProduct) => voucherProduct.voucherType === voucher.type,
          );

          return {
            ...voucher,
            productId: matchedVoucherProduct?.productId ?? null,
          };
        });

        setVoucherList(
          /** 필터링 - (구)인증제, 암송녹음 */
          voucherListWithProductId.filter(
            (voucher) =>
              voucher.type !== 'RECITATION' && voucher.type !== 'RECORDING',
          ) ?? [],
        );
      } catch (error) {
        dispatch(openAlert({ message: getErrorMessages(error) }));
      } finally {
        setIsLoading(false);
      }
    };

    fetchVoucherList();
  }, [lmsLists.course, classId, dispatch]);

  const [selectedVoucherId, setSelectedVoucherId] = useState<number>(
    voucherList[0]?.id,
  );

  const [isMobileHistoryOpen, setIsMobileHistoryOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 1139px)');

    const updateIsMobile = () => {
      setIsMobile(mediaQuery.matches);
    };

    updateIsMobile();

    mediaQuery.addEventListener('change', updateIsMobile);

    return () => {
      mediaQuery.removeEventListener('change', updateIsMobile);
    };
  }, []);

  const selectedVoucher = useMemo(() => {
    if (!selectedVoucherId) return undefined;

    return voucherList.find((voucher) => voucher.id === selectedVoucherId);
  }, [selectedVoucherId]);

  const handleHistoryClick = (voucherId: number) => {
    setSelectedVoucherId(voucherId);
    if (isMobile) {
      setIsMobileHistoryOpen(true);
    }
  };

  const handlePurchaseClick = (voucher: VoucherItem) => {
    const targetUrl = `${process.env.REACT_APP_JEBS_APP_URL}/shop/details/${voucher.productId}`;

    if ('' !== targetUrl) {
      window.open(targetUrl, '_blank');
    } else {
      dispatch(
        openModal({
          body: (
            <ModalAlert message="이용권 구매를 할 수 없습니다. 관리자에게 문의하세요." />
          ),
        }),
      );
    }
  };

  const handleCloseHistoryDrawer = () => {
    setIsMobileHistoryOpen(false);
    setSelectedVoucherId(null);
  };

  if (isLoading) {
    return <LoadingSpinner message="데이터 조회중" />;
  }

  return (
    <div className="flex w-full flex-col pt-6 xl:px-5">
      <header className="flex flex-col gap-3 px-5 xl:flex-row xl:items-center xl:justify-between xl:px-0">
        <div className="min-w-0">
          <TextWinds type="title_h2" xlType="title_h3" className="mb-[2px]">
            이용권 관리
          </TextWinds>

          <TextWinds type="content_body3">
            보유 중인 이용권을 확인하고, 이용권 적립 및 사용 내역을 관리할 수
            있습니다.
          </TextWinds>
        </div>

        <Button
          type="primary"
          className="shrink-0 rounded-lg !border-purple5 !bg-purple5 px-4 hover:!border-purple6 hover:!bg-purple6"
          onClick={() => {
            const url =
              courseType === 'BASIC'
                ? 'https://cafe.naver.com/jemm/5137'
                : 'https://cafe.naver.com/jcebs/26632';
            openNewWindow(url);
          }}
        >
          이용 안내
        </Button>
      </header>

      <div className="my-5 grid grid-cols-1 gap-4 px-3 xl:grid-cols-[1fr_70%] xl:px-0">
        <section className="rounded-xl bg-white p-4 shadow-card xl:p-6">
          <VoucherListPanel
            voucherList={voucherList}
            selectedVoucherId={selectedVoucherId}
            handleHistoryClick={handleHistoryClick}
            handlePurchaseClick={handlePurchaseClick}
          />
        </section>

        {/* PC 이용권 내역 */}
        <section className="hidden rounded-xl bg-white p-4 shadow-card xl:block xl:p-6">
          <VoucherHistoryPanel
            classId={classId}
            selectedVoucher={selectedVoucher}
          />
        </section>
      </div>

      {/* 모바일 이용권 내역 */}
      {isMobile && (
        <VoucherHistoryBottomSheet
          open={isMobileHistoryOpen}
          classId={classId}
          selectedVoucher={selectedVoucher}
          onClose={handleCloseHistoryDrawer}
        />
      )}
    </div>
  );
};

export default VoucherManagement;
